import React from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Award, BarChart3, Calendar, Check, CheckCircle, ChevronRight, Download, FileText, Lightbulb, Sparkles, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { CATEGORY_ICONS, SERVICE_ICONS } from '../../data/assessment-constants';
import { useAssessmentCtx } from './AssessmentContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResultsStep() {
  const { results } = useAssessmentCtx();
  const scoreColor = results.overall_score >= 80 
    ? 'text-emerald-400' 
    : results.overall_score >= 50 
      ? 'text-amber-400' 
      : 'text-red-400';

  const scoreBg = results.overall_score >= 80 
    ? 'from-emerald-500/20 to-teal-500/20 border-emerald-500/30' 
    : results.overall_score >= 50 
      ? 'from-amber-500/20 to-orange-500/20 border-amber-500/30' 
      : 'from-red-500/20 to-rose-500/20 border-red-500/30';

  const labelBg = results.overall_score >= 80 
    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' 
    : results.overall_score >= 50 
      ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' 
      : 'bg-red-500/15 text-red-400 border-red-500/30';

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 py-6 px-4 border-b border-slate-800 bg-slate-950/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-violet-400 text-sm font-semibold">The WE Alignment Index™</p>
            <p className="text-slate-500 text-xs">Your Results</p>
          </div>
          <a 
            href="/"
            className="text-slate-400 hover:text-white text-sm transition-colors"
          >
            Back to Home
          </a>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* PDF Download Banner */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl border border-slate-700 p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="pdf-download-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <FileText className="h-5 w-5 text-violet-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Download Your Full Alignment Report</p>
              <p className="text-slate-400 text-xs">Share with your leadership team</p>
            </div>
          </div>
          <Button
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${API}/api/assessment/${results.id}/pdf`;
              link.download = `WE_Alignment_Index_Report.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              toast.success('Downloading your report...');
            }}
            data-testid="download-pdf-btn"
            className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold px-5 py-2 text-sm whitespace-nowrap"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </div>

        {/* Score Hero */}
        <div className={`bg-gradient-to-br ${scoreBg} rounded-2xl border p-8 sm:p-12 text-center mb-8`} data-testid="results-score-card">
          <div className="mb-6">
            <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Your Overall Alignment Score</p>
            <p className={`text-7xl sm:text-8xl font-bold ${scoreColor}`} data-testid="overall-score">
              {results.overall_score}%
            </p>
          </div>
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${labelBg} border`}>
            {results.overall_score >= 80 ? (
              <CheckCircle className="h-5 w-5" />
            ) : results.overall_score >= 50 ? (
              <TrendingUp className="h-5 w-5" />
            ) : (
              <AlertTriangle className="h-5 w-5" />
            )}
            <span className="font-bold text-lg">{results.label}</span>
          </div>
        </div>

        {/* NEW: Score Interpretation Guide */}
        <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-6 sm:p-8 mb-8" data-testid="score-interpretation-guide">
          <h2 className="text-white font-bold text-xl mb-6 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-cyan-400" />
            What Your Score Means
          </h2>
          <div className="space-y-4">
            <div className={`p-4 rounded-xl border ${results.overall_score >= 80 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-bold ${results.overall_score >= 80 ? 'text-emerald-400' : 'text-slate-400'}`}>80-100%</span>
                <span className={`text-sm ${results.overall_score >= 80 ? 'text-emerald-400' : 'text-slate-500'}`}>High Alignment</span>
              </div>
              <p className={`text-sm ${results.overall_score >= 80 ? 'text-emerald-300' : 'text-slate-500'}`}>
                Strong foundation in place. Focus on optimization and scaling what works.
              </p>
            </div>
            <div className={`p-4 rounded-xl border ${results.overall_score >= 60 && results.overall_score < 80 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-bold ${results.overall_score >= 60 && results.overall_score < 80 ? 'text-amber-400' : 'text-slate-400'}`}>60-79%</span>
                <span className={`text-sm ${results.overall_score >= 60 && results.overall_score < 80 ? 'text-amber-400' : 'text-slate-500'}`}>Moderate Alignment</span>
              </div>
              <p className={`text-sm ${results.overall_score >= 60 && results.overall_score < 80 ? 'text-amber-300' : 'text-slate-500'}`}>
                Gaps are already costing you productivity and performance. Action needed.
              </p>
            </div>
            <div className={`p-4 rounded-xl border ${results.overall_score < 60 ? 'bg-red-500/10 border-red-500/30' : 'bg-slate-800/30 border-slate-700/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`font-bold ${results.overall_score < 60 ? 'text-red-400' : 'text-slate-400'}`}>Below 60%</span>
                <span className={`text-sm ${results.overall_score < 60 ? 'text-red-400' : 'text-slate-500'}`}>Critical Gaps</span>
              </div>
              <p className={`text-sm ${results.overall_score < 60 ? 'text-red-300' : 'text-slate-500'}`}>
                Major breakdowns in leadership and execution. Urgent intervention recommended.
              </p>
            </div>
          </div>
        </div>

        {/* PERSONALIZED SCORE-BASED INSIGHT */}
        <div className={`rounded-2xl border-2 p-6 sm:p-8 mb-8 ${
          results.overall_score < 60 
            ? 'bg-gradient-to-r from-red-600/15 to-red-500/10 border-red-500/40' 
            : results.overall_score < 80 
              ? 'bg-gradient-to-r from-amber-600/15 to-amber-500/10 border-amber-500/40'
              : 'bg-gradient-to-r from-emerald-600/15 to-emerald-500/10 border-emerald-500/40'
        }`} data-testid="personalized-insight">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              results.overall_score < 60 
                ? 'bg-red-500/20' 
                : results.overall_score < 80 
                  ? 'bg-amber-500/20'
                  : 'bg-emerald-500/20'
            }`}>
              {results.overall_score < 60 ? (
                <AlertTriangle className="h-6 w-6 text-red-400" />
              ) : results.overall_score < 80 ? (
                <TrendingUp className="h-6 w-6 text-amber-400" />
              ) : (
                <CheckCircle className="h-6 w-6 text-emerald-400" />
              )}
            </div>
            <div>
              <p className={`text-sm font-bold uppercase tracking-wide mb-2 ${
                results.overall_score < 60 
                  ? 'text-red-400' 
                  : results.overall_score < 80 
                    ? 'text-amber-400'
                    : 'text-emerald-400'
              }`}>Your Personalized Insight</p>
              <p className="text-white text-lg leading-relaxed font-medium">
                {results.overall_score < 60 
                  ? "Your results show significant breakdowns in alignment that are likely impacting performance."
                  : results.overall_score < 80 
                    ? "Your organization has moderate alignment, but key gaps are slowing execution."
                    : "You have strong alignment. Now it's about optimizing performance and scaling."
                }
              </p>
            </div>
          </div>
        </div>

        {/* HIGH-CONVERSION BOOKING SECTION - Immediately after score */}
        <div className="bg-gradient-to-br from-violet-900/40 via-slate-900 to-cyan-900/40 rounded-2xl border-2 border-violet-500/50 p-8 sm:p-10 mb-8 text-center" data-testid="conversion-booking-section">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Your Results Are Clear… Now Let's Break It Down
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            Most leaders misinterpret their results or miss the biggest opportunity for improvement.
            <span className="text-white font-semibold"> Let's walk through this together.</span>
          </p>

          {/* Embedded Calendly */}
          <div className="bg-white rounded-xl overflow-hidden mb-6 max-w-2xl mx-auto shadow-2xl shadow-violet-500/20" data-testid="primary-calendly-embed">
            <iframe 
              src="https://calendly.com/coachbrandonward-1/schedule_with_cbw?hide_landing_page_details=1&hide_gdpr_banner=1&background_color=ffffff&text_color=1e293b&primary_color=8b5cf6"
              width="100%"
              height="650"
              frameBorder="0"
              title="Book Leadership Alignment Debrief"
              style={{ minWidth: '320px' }}
            />
          </div>

          <p className="text-slate-500 text-sm">
            Free 20-30 minute session • No obligation • Instant clarity
          </p>
        </div>

        {/* Interpretation */}
        <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-6 sm:p-8 mb-8" data-testid="results-interpretation">
          <h2 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-400" />
            What This Means
          </h2>
          <p className="text-slate-300 leading-relaxed">
            {results.interpretation}
          </p>
        </div>

        {/* Recommended Next Step - Service Recommendation */}
        {results.service_recommendation && (
          <div 
            className="relative bg-gradient-to-br from-violet-600/10 via-slate-900/80 to-cyan-600/10 rounded-2xl border-2 border-violet-500/40 p-6 sm:p-8 mb-8 overflow-hidden"
            data-testid="service-recommendation"
          >
            {/* Glow effect */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-violet-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl" />

            {/* Badge */}
            <div className="relative flex items-center gap-2 mb-6">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/30">
                <Lightbulb className="h-4 w-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-bold uppercase tracking-wide">Recommended Next Step</span>
              </div>
            </div>

            <div className="relative">
              {/* Personalized Intro */}
              <p className="text-slate-300 leading-relaxed mb-6 text-lg">
                {results.service_recommendation.personalized_intro?.replace(/\*\*/g, '')}
              </p>

              {/* Urgency Message */}
              <p className="text-slate-400 text-sm mb-6 italic">
                {results.service_recommendation.urgency_message}
              </p>

              {/* Program Card */}
              <div className="bg-slate-950/60 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500/30 to-cyan-500/30 flex items-center justify-center border border-violet-500/30">
                      {(() => {
                        const IconComponent = SERVICE_ICONS[results.service_recommendation.icon] || Target;
                        return <IconComponent className="h-8 w-8 text-violet-400" />;
                      })()}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                      {results.service_recommendation.program_name}
                    </h3>
                    <p className="text-slate-300 mb-4">
                      {results.service_recommendation.description}
                    </p>

                    {/* Why It Matters */}
                    <div className="p-4 rounded-lg bg-violet-500/5 border border-violet-500/20 mb-4">
                      <p className="text-sm font-semibold text-violet-300 mb-1 uppercase tracking-wide">Why This Matters</p>
                      <p className="text-slate-300 text-sm leading-relaxed">
                        {results.service_recommendation.why_it_matters}
                      </p>
                    </div>

                    {/* Key Outcomes */}
                    {results.service_recommendation.key_outcomes && results.service_recommendation.key_outcomes.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-cyan-300 mb-3 uppercase tracking-wide">Expected Outcomes</p>
                        <div className="space-y-2">
                          {results.service_recommendation.key_outcomes.map((outcome) => (
                            <div key={outcome} className="flex items-center gap-3">
                              <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                <Check className="h-3 w-3 text-cyan-400" />
                              </div>
                              <span className="text-slate-300 text-sm">{outcome}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Business Impact */}
                    {results.service_recommendation.business_impact && (
                      <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 mb-6">
                        <p className="text-emerald-400 text-sm font-medium flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          {results.service_recommendation.business_impact}
                        </p>
                      </div>
                    )}

                    {/* CTA */}
                    <a
                      href={results.calendly_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="recommendation-cta-btn"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40"
                    >
                      {results.service_recommendation.cta_text || "Schedule Your Strategy Session"}
                      <ArrowRight className="h-4 w-4" />
                    </a>
                    <p className="text-slate-500 text-xs mt-3">
                      {results.service_recommendation.cta_subtext || "Discuss how we can help address this specific challenge"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-6 sm:p-8 mb-8" data-testid="results-breakdown">
          <h2 className="text-white font-bold text-xl mb-6 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-cyan-400" />
            Category Breakdown
          </h2>
          <div className="space-y-5">
            {Object.entries(results.category_scores).map(([category, data]) => {
              const CategoryIcon = CATEGORY_ICONS[category] || Target;
              const barColor = data.score >= 70 
                ? 'from-emerald-500 to-teal-500' 
                : data.score >= 50 
                  ? 'from-amber-500 to-orange-500' 
                  : 'from-red-500 to-rose-500';

              return (
                <div key={category}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <CategoryIcon className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-300 font-medium text-sm">{category}</span>
                    </div>
                    <span className={`font-bold ${data.score >= 70 ? 'text-emerald-400' : data.score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {data.score}%
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
                    <div 
                      className={`h-full rounded-full bg-gradient-to-r ${barColor} transition-all duration-1000`}
                      style={{ width: `${data.score}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key Insights */}
        {results.insights && results.insights.length > 0 && (
          <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border border-slate-800 p-6 sm:p-8 mb-8" data-testid="results-insights">
            <h2 className="text-white font-bold text-xl mb-6 flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-400" />
              Your Top 3 Opportunities for Growth
            </h2>
            <div className="space-y-4">
              {results.insights.map((insight, index) => (
                <div 
                  key={insight.title || index}
                  className="p-5 rounded-xl bg-slate-950/50 border border-slate-800"
                  data-testid={`insight-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0 text-amber-400 font-bold text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-white font-semibold">{insight.title}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">
                          {insight.score}%
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm mb-3">{insight.description}</p>
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
                        <ChevronRight className="h-4 w-4 text-violet-400 mt-0.5 flex-shrink-0" />
                        <p className="text-violet-300 text-sm">{insight.recommendation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Social Proof / Credibility */}
        <div className="bg-gradient-to-r from-violet-500/5 to-cyan-500/5 rounded-2xl border border-slate-800 p-6 sm:p-8 mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 text-center md:text-left">
              <p className="text-slate-400 text-sm mb-2">Trusted by organizations like</p>
              <p className="text-white font-semibold">UNLV, Clark County School District, City of Las Vegas, and more</p>
            </div>
            <div className="flex gap-4 text-center">
              <div className="px-4">
                <p className="text-2xl font-bold text-cyan-400">500+</p>
                <p className="text-slate-500 text-xs">Leaders Trained</p>
              </div>
              <div className="px-4 border-l border-slate-800">
                <p className="text-2xl font-bold text-violet-400">15+</p>
                <p className="text-slate-500 text-xs">Years Experience</p>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: Soft Paywall - Leadership Debrief */}
        <div className="bg-gradient-to-br from-slate-900 via-violet-900/20 to-slate-900 rounded-2xl border-2 border-violet-500/40 p-8 sm:p-10 mb-8" data-testid="leadership-debrief-section">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/15 border border-violet-500/30 mb-6">
              <Award className="h-4 w-4 text-violet-400" />
              <span className="text-violet-300 text-sm font-bold uppercase tracking-wide">The Real Solution</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Most Leaders Only Scratch the Surface
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              Your score reveals the symptoms. Our <span className="text-white font-semibold">Leadership Alignment Debrief</span> uncovers the root causes and shows you exactly what to do next.
            </p>
          </div>

          {/* What You Get */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              { title: "Deep-Dive Analysis", desc: "We break down every category score and explain what's really happening" },
              { title: "Root Cause Identification", desc: "Discover the hidden patterns driving your alignment gaps" },
              { title: "Action Roadmap", desc: "Get specific, prioritized steps to improve immediately" },
              { title: "Strategic Guidance", desc: "One-on-one time with Brandon to answer your questions" }
            ].map((item) => (
              <div key={item.title} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                    <Check className="h-4 w-4 text-emerald-400" />
                  </div>
                  <span className="text-white font-semibold">{item.title}</span>
                </div>
                <p className="text-slate-400 text-sm pl-11">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Embedded Calendly CTA */}
        <div className="bg-gradient-to-br from-violet-600/20 to-cyan-600/20 rounded-2xl border border-violet-500/30 p-8 sm:p-10 text-center" data-testid="results-cta">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-6 border border-violet-500/30">
            <Calendar className="h-8 w-8 text-violet-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Book Your Leadership Debrief Now
          </h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">
            Don't just see the score — understand it. Get your personalized breakdown and action plan.
          </p>

          {/* Embedded Calendly */}
          <div className="bg-white rounded-xl overflow-hidden mb-6 max-w-2xl mx-auto" data-testid="calendly-embed">
            <iframe 
              src="https://calendly.com/coachbrandonward-1/schedule_with_cbw?hide_landing_page_details=1&hide_gdpr_banner=1&background_color=ffffff&text_color=1e293b&primary_color=8b5cf6"
              width="100%"
              height="650"
              frameBorder="0"
              title="Schedule Leadership Debrief"
              style={{ minWidth: '320px' }}
            />
          </div>

          <p className="text-slate-500 text-sm">
            Free 20-30 minute session. No obligation. Instant clarity.
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <a 
            href="/"
            className="text-slate-400 hover:text-white text-sm transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Culture Align
          </a>
        </div>
      </main>
    </div>
  );
}
